'use no memo';

import {showNotification} from 'platform/components';

import {useDispatch} from 'react-redux';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {omneticApi} from '@dms/api/base';
import i18n from '@dms/i18n';

import {notificationTypes} from '../constants/notificationTypes';

interface UseVehicleAlbumCopyNotificationProps {
  vehicleId: string;
  /**
   * Gates only the user-facing affordances (success toast + `onCopyFinished` placeholder
   * clearing) to galleries that can actually initiate a copy (i.e. show the move/copy menu).
   * Cache invalidation is NOT gated by this — a finished copy genuinely changed the album
   * contents for the vehicle, so whichever gallery for that vehicle is mounted refreshes the
   * affected albums. Otherwise, if the copy-capable gallery unmounts before the push lands,
   * the album caches would stay stale until an unrelated action busted them.
   */
  enabled: boolean;
  /**
   * Called with the destination album id when a copy into this vehicle finishes,
   * so the caller can clear the optimistic "copying" placeholder for that album.
   */
  onCopyFinished?: (destinationAlbumId: string) => void;
}

/**
 * Service-case photo copy completes asynchronously on the backend, so the copy PUT only
 * returns "accepted" (204). The backend later pushes a `vehicle_photo-copy_finished`
 * background notification once the copied photos exist. This hook listens for it and, for
 * the current vehicle, refreshes exactly the source and destination albums and shows a
 * success toast.
 */
export const useVehicleAlbumCopyNotification = (props: UseVehicleAlbumCopyNotificationProps) => {
  const dispatch = useDispatch();

  useSubscribeToNotificationsQuery((notification) => {
    if (notification.type !== notificationTypes.VEHICLE_PHOTO_COPY_FINISHED) {
      return;
    }
    if (notification.payload.vehicle.id !== props.vehicleId) {
      return;
    }

    const {sourceAlbumId, destinationAlbumId} = notification.payload;

    // Always refresh the affected albums for this vehicle — the copied photo changed their
    // contents regardless of which gallery view happens to be mounted (see `enabled` doc).
    dispatch(
      omneticApi.util.invalidateTags([
        {type: 'VehicleAlbumPhotos', id: sourceAlbumId},
        {type: 'VehicleAlbumPhotos', id: destinationAlbumId},
        {type: 'VehicleAlbumList', id: props.vehicleId},
      ])
    );

    // Toast + placeholder clearing are UI affordances only for the copy-capable view.
    if (!props.enabled) {
      return;
    }

    showNotification.success(i18n.t('entity.photo.notifications.copySuccess'));

    props.onCopyFinished?.(destinationAlbumId);
  });
};
